import React, { useState, useCallback, useRef, useEffect } from 'react';
import './DiscreteFilterUI.scss';
import { Duration } from '../../duration';

export interface DiscreteFilterUIProps<T = unknown> {
  columnName: string;
  values: T[];
  selectedValues: Set<T>;
  onSelectionChange: (selected: Set<T>) => void;
  getLabel: (value: T) => string;
}

export function DiscreteFilterUI<T = unknown>(props: DiscreteFilterUIProps<T>) {
  const { columnName, values, selectedValues, onSelectionChange, getLabel } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const tooltipTimerRef = useRef<number>();

  // Close menu when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleToggle = useCallback((value: T) => {
    const newSelected = new Set(selectedValues);
    if (newSelected.has(value)) {
      newSelected.delete(value);
    } else {
      newSelected.add(value);
    }
    onSelectionChange(newSelected);
  }, [selectedValues, onSelectionChange]);

  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(values));
  }, [values, onSelectionChange]);

  const handleClearAll = useCallback(() => {
    onSelectionChange(new Set());
  }, [onSelectionChange]);

  const handleMouseEnter = useCallback(() => {
    const tooltipDelay = Duration.fromMilliseconds(250);
    tooltipTimerRef.current = window.setTimeout(() => {
      setShowTooltip(true);
    }, tooltipDelay.toMilliseconds());
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setShowTooltip(false);
  }, []);

  const selectedCount = selectedValues.size;
  const totalCount = values.length;

  let buttonLabel: string;
  if (selectedCount === 0 || selectedCount === totalCount) {
    buttonLabel = `${columnName}: All`;
  } else if (selectedCount === 1) {
    const selectedValue = Array.from(selectedValues)[0];
    buttonLabel = `${columnName}: ${getLabel(selectedValue)}`;
  } else {
    buttonLabel = `${columnName} (${selectedCount} of ${totalCount} selected)`;
  }

  // Create tooltip showing selected values (up to 10, then show count)
  let tooltipText: string;
  if (selectedCount === 0 || selectedCount === totalCount) {
    tooltipText = `All ${columnName.toLowerCase()} selected`;
  } else {
    const selectedLabels = Array.from(selectedValues).map(v => getLabel(v)).sort();
    const maxShow = 10;
    if (selectedLabels.length <= maxShow) {
      tooltipText = `${columnName}:\n${selectedLabels.join('\n')}`;
    } else {
      const shown = selectedLabels.slice(0, maxShow);
      const remaining = selectedLabels.length - maxShow;
      tooltipText = `${columnName}:\n${shown.join('\n')}\n... and ${remaining} more`;
    }
  }

  if (values.length === 0) {
    return null;
  }

  return (
    <div className="discrete-filter" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="discrete-filter__button"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {buttonLabel}
      </button>

      {showTooltip && !isOpen && (
        <div className="discrete-filter__tooltip">
          {tooltipText.split('\n').map((line, idx) => (
            <div key={idx}>{line}</div>
          ))}
        </div>
      )}

      {isOpen && (
        <>
          <div className="discrete-filter__backdrop" onClick={() => setIsOpen(false)} />
          <div className="discrete-filter__menu">
            <div className="discrete-filter__header">
              <span>{columnName}</span>
              <div className="discrete-filter__actions">
                <button onClick={handleSelectAll} className="discrete-filter__action">
                  All
                </button>
                <button onClick={handleClearAll} className="discrete-filter__action">
                  None
                </button>
              </div>
            </div>
            <div className="discrete-filter__items">
              {values.map((value, idx) => {
                const label = getLabel(value);
                return (
                  <label key={idx} className="discrete-filter__item">
                    <input
                      type="checkbox"
                      checked={selectedValues.has(value)}
                      onChange={() => handleToggle(value)}
                    />
                    <span>{label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
